import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, ArrowDown, ArrowUp, ChevronLeft, ChevronRight, FileSpreadsheet, Loader2, RotateCcw, Upload } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';
import { OknoWyboruGrupy, OknoZajetejKarty, type ZajetaKarta } from '@/components/OknaBleduImportu';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, FormGrid, ModalError } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Tabs } from '@/components/ui/tabs';
import { useToast } from '@/components/ui/toast';
import { ApiError } from '@/lib/api';
import { useI18n } from '@/lib/i18n';
import {
  czyPierwszyToNaglowek,
  czytajArkusze,
  kartaMozeBycObcieta,
  podpowiedzMapowania,
  wierszeZMapowania,
  type Arkusz,
  type Mapowanie,
  type RodzajKomorki,
} from '@/lib/arkusz-pracownikow';
import {
  fetchCostCenterSuggestions,
  fetchEmployeeGroups,
  importujPracownikow,
  pracownicyPoNumerach,
} from '@/lib/employees-api';
import { zeSchowka } from '@/lib/wklejka-tabeli';
import { cn } from '@/lib/utils';
import {
  KLUCZE_POL,
  MAX_WSAD,
  podmienPole,
  POLA_IMPORTU,
  przestaw,
  bezZmianDoOdznaczenia,
  bledneDoOdznaczenia,
  ocenWsad,
  powtorzoneDoOdznaczenia,
  podzielWiersze,
  zPozycji,
  type KluczPola,
  type OcenaWsadu,
  type SurowyWiersz,
} from '@/lib/import-pracownikow';

/**
 * IMPORT WIELU PRACOWNIKÓW — WKLEJONA LISTA ALBO ARKUSZ EXCEL.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  ⚠️⚠️ ZAMÓWIONE 04.09.2026: „obsługa dodawania wielu pracowników jednocześnie przez pole
 *  tekstowe oraz import pliku z pracownikami w excelu […] a obok podgląd co na co się zmapuje".
 *
 *  ⚠️⚠️ PODGLĄD JEST TU NAJWAŻNIEJSZĄ CZĘŚCIĄ, nie ozdobą. Kolejność pozycji jest sztywna
 *  (numer karty, imię i nazwisko, alias, centrum kosztowe, GRUPA, pozwól logować, wyloguj po
 *  operacji, ograniczenie do pojedynczej przegrody, polityka wydzielania), a numer karty jest
 *  kluczem naturalnym w czterech tabelach SPS. Wsad zmapowany o jedno pole w bok zakłada
 *  kilkuset ludzi z przesuniętymi uprawnieniami i nie ma tego jak odkręcić jednym ruchem.
 *  Dlatego widać KAŻDĄ wartość i to, czy pochodzi z wiersza, czy jest domyślna.
 *
 *  ⚠️ ALBO CAŁY WSAD, ALBO NIC — rozstrzygnięte z użytkownikiem. Cały import to JEDNA operacja
 *  config-down, którą agent stosuje w jednej transakcji na bazie SPS. Przycisk jest więc martwy,
 *  dopóki KAŻDY wiersz nie jest poprawny; „wyślij te dobre" dałoby kadrę bez części ludzi.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */
type Zrodlo = 'tekst' | 'plik';

export function ImportPracownikow({
  groupId,
  automaty,
  onClose,
  onSaved,
}: {
  groupId: string;
  /** Automaty wyspy — do przypisania bezpośredniego, domyślnie wszystkie. */
  automaty: readonly { id: string; nazwa: string }[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useI18n();
  const toast = useToast();
  const [zrodlo, setZrodlo] = useState<Zrodlo>('tekst');
  const [tekst, setTekst] = useState('');
  const [arkusze, setArkusze] = useState<Arkusz[] | null>(null);
  const [nazwaPliku, setNazwaPliku] = useState('');
  const [wybranyArkusz, setWybranyArkusz] = useState(0);
  const [mapowanie, setMapowanie] = useState<Mapowanie>({});
  const [zNaglowkiem, setZNaglowkiem] = useState(true);
  const [wybraneAutomaty, setWybraneAutomaty] = useState<string[]>(automaty.map((m) => m.id));
  const [wysylka, setWysylka] = useState(false);
  const [blad, setBlad] = useState<string | null>(null);
  const [ciagnieta, setCiagnieta] = useState<number | null>(null);
  /**
   * ═════════════════════════════════════════════════════════════════
   *  NADPISYWANIE ISTNIEJĄCYCH — TYLKO PRZY ARKUSZU I TYLKO ZA ZGODĄ.
   *
   *  ⚠️⚠️ ZGŁOSZONE 04.09.2026: „firmy używają tych samych kart, a jak wskakuje nowy pracownik, to
   *  zmieniają tam tylko imię i nazwisko i grupę pracowniczą". Czyli plik z tą samą kartą i innym
   *  nazwiskiem jest poprawnym wsadem, a nie pomyłką.
   *
   *  ⚠️⚠️ DOPRECYZOWANE TEGO SAMEGO DNIA: „dajmy tylko do importu z arkusza i to nie jako
   *  domyślne". Wklejanie tekstem zostało więc CZYSTYM zakładaniem — i to jest słuszne: wklejenie
   *  jest gestem dorywczym, robionym z ręki, a plik od firmy kadrowej jest świadomym wsadem.
   *  Nadpisanie kadry jest nieodwracalne jednym ruchem, więc droga do niego ma być ta wolniejsza.
   * ═════════════════════════════════════════════════════════════════
   */
  const [nadpisuj, setNadpisuj] = useState(false);
  /**
   * ⚠️⚠️ OKNA PRZY BŁĘDACH PODGLĄDU — zamówione 08.09.2026. Podgląd mówił, CO jest źle, i na tym
   * kończył: „ten numer karty jest już używany" nie odpowiadało na „przez kogo", a błąd grupy
   * kazał pamiętać z głowy, jakie grupy wyspa w ogóle ma.
   */
  const [zajetaKarta, setZajetaKarta] = useState<string | null>(null);
  const [grupaDlaWiersza, setGrupaDlaWiersza] = useState<{ nr: number; wpisane: string } | null>(null);
  /**
   * ⚠️ ZAMAWIANE JAKO PRZEŁĄCZNIK: „czasami jest też aktualizacja grup pracownika i wtedy raczej
   * powinno podmieniać na nową usuwając starą, ale dobrze by było mieć na to jakiś checkbox".
   * Stąd domyślnie WŁĄCZONY — „raczej powinno podmieniać" — ale do zdjęcia jednym kliknięciem.
   *
   * ⚠️ DZIAŁA WYŁĄCZNIE NA AKTUALIZACJACH: u nowego pracownika nie ma czego zastępować.
   */
  const [grupyZastapuj, setGrupyZastapuj] = useState(true);
  /**
   * ═══════════════════════════════════════════════════════════════
   *  ZAKRES WIERSZY BRANYCH Z ARKUSZA.
   *
   *  ⚠️⚠️ ZAMÓWIONE 04.09.2026: „zamiast 1 wiersza dostajemy 2500 wierszy — chyba trzeba jakiś
   *  ogranicznik, ile wierszy bierzemy, jak pojawi się taki błąd".
   *
   *  ⚠️ ZAKRES, NIE SAM LIMIT „pierwszych N": arkusze klientów miewają nad danymi wstęp albo drugi
   *  nagłówek, a wtedy „pierwszych N" bierze śmieci i pomija ludzi. Pole „od" kosztuje jedną
   *  kontrolkę więcej i załatwia oba przypadki.
   *
   *  ⚠️ PUSTE ZNACZY „DO KOŃCA" — zero w tym polu znaczyłoby „nie bierz nic", a to inna intencja.
   * ═══════════════════════════════════════════════════════════════
   */
  const [odWiersza, setOdWiersza] = useState('');
  const [doWiersza, setDoWiersza] = useState('');
  /**
   * ⚠️⚠️ WIERSZE ZDJĘTE ZE WSADU RĘKĄ — zamówione: „czy możemy zrobić jakiś mechanizm, który
   * pozwoli odznaczyć błędny wiersz?". Trzymamy NUMERY WIERSZY Z ARKUSZA, nie pozycje w tabeli:
   * pozycje zmieniają się przy każdej zmianie mapowania albo zakresu, a numer wiersza jest tym,
   * co człowiek widzi w Excelu i po czym rozmawia z klientem przez telefon.
   */
  const [wykluczone, setWykluczone] = useState<ReadonlySet<number>>(new Set());
  /**
   * ═══════════════════════════════════════════════════════════════
   *  KOLEJNOŚĆ POZYCJI W WIERSZU — PRZESTAWIALNA OD 07.09.2026.
   *
   *  ⚠️⚠️ DECYZJA ZMIENIONA, i to trzeba powiedzieć wprost: 04.09.2026 rozstrzygnięto „tu chcemy
   *  na sztywno", gdy proponowałem mapowanie do zmiany. Po pierwszych realnych plikach wróciło
   *  „fajnie by było jeszcze, żeby zamieniać kolumny miejscami, bo różni klienci nam różnie to
   *  wysyłają". Przesłanka się zmieniła: format nie jest własnością portalu, a klienta.
   *
   *  ⚠️ NIE ZAPAMIĘTUJEMY MIĘDZY OTWARCIAMI. Kolejność jest cechą PLIKU, który właśnie wklejasz,
   *  nie wyspy — a zapamiętana po cichu przestawiałaby pola w następnym, INNYM wsadzie. Gdyby
   *  okazało się, że jeden klient przysyła zawsze ten sam układ, właściwym miejscem na to jest
   *  ustawienie wyspy po stronie serwera, nie pamięć przeglądarki jednej osoby.
   * ═══════════════════════════════════════════════════════════════
   */
  const [kolejnosc, setKolejnosc] = useState<KluczPola[]>([...KLUCZE_POL]);
  /**
   * ⚠️⚠️ OBCINANIE ZER WIODĄCYCH — DOMYŚLNIE WYŁĄCZONE I TO JEST KONIECZNOŚĆ, nie ostrożność.
   * Zamówione: „niektórzy klienci wysyłają 0000AAA […] a czytnik w automacie jest skonfigurowany
   * na usuwanie zer wiodących". ALE arkusz Magny ma numery w postaci `000001234`, gdzie zera SĄ CZĘŚCIĄ
   * KLUCZA — obcinanie ich założyłoby ludzi pod numerem `1234`, czyli pod innym kluczem niż ten
   * na karcie. Przełącznik musi więc być świadomym kliknięciem, a nie stanem domyślnym.
   */
  const [usunZera, setUsunZera] = useState(false);
  /** Przewijanie rynny z numerami wierszy trzyma się przewijania pola — patrz komentarz przy rynnie. */
  const [przewiniecie, setPrzewiniecie] = useState(0);
  const plikRef = useRef<HTMLInputElement>(null);
  const polePreRef = useRef<HTMLTextAreaElement>(null);

  /**
   * ⚠️⚠️ NAZWY GRUP ROZWIĄZUJEMY NA EKRANIE, nie w API. Dzięki temu podgląd mówi „nie ma grupy
   * »MONTER«" NATYCHMIAST, przed jakimkolwiek żądaniem — a API i tak sprawdza identyfikatory
   * po swojemu, więc nie jest to jedyna bariera.
   *
   * ⚠️ SZUKAMY PO NAZWIE I PO KODZIE, bo nie wiemy, które podaje klient: arkusz Magny daje nazwę
   * (`PRACOWNICY AB-CD`), tabela z maila kod (`MONTER`).
   */
  const grupy = useQuery({
    queryKey: ['employee-groups', 'do-importu', groupId],
    queryFn: () => fetchEmployeeGroups(groupId),
    staleTime: 60_000,
  });

  /**
   * ⚠️⚠️ ISTNIEJĄCE NUMERY KART — po to, żeby kolizję było widać W PODGLĄDZIE, a nie dopiero
   * w odmowie całego wsadu. Przy 200 wierszach komunikat „numer zajęty" bez wskazania wiersza
   * kazałby szukać po kolei.
   *
   * ⚠️ JEDNYM ODCZYTEM, DO 500 OSÓB — tyle wpuszcza `pageSize` w API. Powyżej tego kolizję
   * wyłapie dopiero instancja i podgląd mówi to wprost (`imp.zajeta.nieWiemyKto`); nie ciągniemy
   * całej kadry na ekran po to, żeby ją tam wyrzucić.
   */

  /**
   * ═══════════════════════════════════════════════════════════════════════════
   *  KODY CENTRÓW, KTÓRE JUŻ SĄ NA WYSPIE — pod żółty znaczek „to centrum jeszcze nie istnieje".
   *
   *  ⚠️⚠️ TA TRASA, A NIE SŁOWNIK `UserCostCenters`. Słownik jest u klientów PUSTY (zmierzone:
   *  zero encji przy kilkuset osobach z kodem wpisanym z ręki), więc porównanie z nim uznałoby
   *  KAŻDY kod za nowy — i znaczek byłby przy każdym wierszu. Ta trasa liczy kody z tego, co
   *  REALNIE stoi u ludzi, plus słownik.
   *
   *  ⚠️ LICZY SERWER PO CAŁEJ KADRZE, więc znaczek nie zależy od tego, ile osób zmieściło się
   *  w liście powyżej (500). Kolizja numeru karty ma tę słabość i musi ją zgłaszać; tutaj jej nie ma.
   * ═══════════════════════════════════════════════════════════════════════════
   */
  const centra = useQuery({
    queryKey: ['employees', 'centra-do-importu', groupId],
    queryFn: () => fetchCostCenterSuggestions(groupId),
    staleTime: 60_000,
  });

  const arkusz = arkusze?.[wybranyArkusz] ?? null;
  const naglowki = arkusz?.wiersze[0] ?? [];
  /**
   * Wiersze DANYCH z wybranego arkusza, przycięte zakresem.
   *
   * ⚠️ NUMERY LICZĄ SIĘ OD ARKUSZA (nagłówek to wiersz 1), żeby „wiersz 2500" w komunikacie
   * wskazywał to, co człowiek widzi w Excelu — a nie pozycję w naszej tabeli.
   */
  const zakres = useMemo(() => {
    if (arkusz === null) return { wiersze: [] as string[][], rodzaje: [] as RodzajKomorki[][], pierwszyNr: 1 };
    const przesuniecie = zNaglowkiem ? 1 : 0;
    const od = Math.max(przesuniecie + 1, Number(odWiersza) || przesuniecie + 1);
    const doKonca = arkusz.wiersze.length;
    const doK = Math.min(doKonca, Number(doWiersza) || doKonca);
    const poczatek = od - 1;
    const koniec = Math.max(poczatek, doK);
    return {
      wiersze: arkusz.wiersze.slice(poczatek, koniec),
      rodzaje: arkusz.rodzaje.slice(poczatek, koniec),
      pierwszyNr: poczatek + 1,
    };
  }, [arkusz, zNaglowkiem, odWiersza, doWiersza]);

  /** Surowe wiersze z obu dróg — dalej wszystko jest wspólne. */
  const zArkusza = useMemo(
    () => wierszeZMapowania(zakres.wiersze, mapowanie, zakres.rodzaje, zakres.pierwszyNr),
    [zakres, mapowanie],
  );
  const surowe: { nr: number; wiersz: SurowyWiersz }[] = useMemo(() => {
    if (zrodlo === 'tekst') {
      /** ⚠️ `kolejnosc` to jedyne miejsce, w którym pozycja wiersza staje się nazwą pola. */
      return podzielWiersze(tekst).wiersze.map((w) => ({ nr: w.nr, wiersz: zPozycji(w.pola, kolejnosc) }));
    }
    return zArkusza.wiersze;
  }, [zrodlo, tekst, zArkusza, kolejnosc]);

  const separator = useMemo(() => podzielWiersze(tekst).separator, [tekst]);

  /**
   * ══════════════════════════════════════════════════════════════════════════════════════════════
   *  PYTAMY O NUMERY Z WSADU, A NIE O PIERWSZĄ STRONĘ KADRY.
   *
   *  ⚠️⚠️ POPRZEDNIA WERSJA BRAŁA `pageSize: 500` posortowane po numerze karty rosnąco — i to był
   *  błąd zgłoszony 18.09.2026 („fajnie, jakby dało się pokazać live, że dany numer karty już
   *  istnieje"). Przy kadrze większej niż 500 osoby o WYSOKICH numerach wypadały poza tę stronę,
   *  więc podgląd ogłaszał je jako nowe: bez plakietki „nadpisanie", bez różnicy „stare → nowe"
   *  i bez ostrzeżenia. Serwer przy zapisie czytał wszystkich, czyli EKRAN MÓWIŁ CO INNEGO NIŻ ZAPIS
   *  — a to jest ta klasa kłamstwa, po której przestaje się wierzyć podglądowi.
   *
   *  ⚠️ KLUCZ ZAPYTANIA Z POSORTOWANYCH, UNIKALNYCH NUMERÓW. Przy wklejaniu tekstu wsad zmienia się
   *  z każdym znakiem; po takim kluczu przeglądarka odpytuje raz na FAKTYCZNĄ zmianę zestawu kart,
   *  a nie raz na naciśnięcie klawisza.
   * ══════════════════════════════════════════════════════════════════════════════════════════════
   */
  const numeryWsadu = useMemo(() => {
    const zbior = new Set(
      surowe.map((w) => w.wiersz.karta.trim().toLowerCase()).filter((n) => n !== ''),
    );
    return [...zbior].sort();
  }, [surowe]);

  const istniejace = useQuery({
    queryKey: ['employees', 'po-numerach', groupId, numeryWsadu],
    queryFn: () => pracownicyPoNumerach(groupId, numeryWsadu),
    /** ⚠️ Pusty wsad to nie jest pytanie — `enabled` trzyma stan „nie wiemy" zamiast udawać „nic nie ma". */
    enabled: numeryWsadu.length > 0,
    staleTime: 30_000,
  });


  const ocena: OcenaWsadu = useMemo(
    () =>
      ocenWsad(surowe, {
        istniejaceKarty: (istniejace.data?.rows ?? []).map((e) => e.spsUserNumber),
        /**
         * ⚠️⚠️ OBECNE WARTOŚCI Z TEJ SAMEJ LISTY, którą ekran już ma — żadnego nowego odczytu.
         * Po nich podgląd pokazuje „stare → nowe" i rozpoznaje wiersz, który nic nie zmienia.
         *
         * ⚠️ `active` TO `Users.ALLOWLOGIN`, a `rationEffect` to polityka wydzielania — nazwy
         * na liście kadry i w imporcie różnią się świadomie, więc mapujemy je tutaj, w jednym
         * miejscu, zamiast zgadywać przy każdym porównaniu.
         */
        obecni: new Map(
          (istniejace.data?.rows ?? []).map((e) => [
            e.spsUserNumber.trim().toLowerCase(),
            {
              nazwa: e.name,
              alias: e.alias,
              centrum: e.costCenter,
              logowanie: e.active,
              polityka: e.rationEffect,
              grupy: e.groups.map((g) => g.name),
            },
          ]),
        ),
        grupyZastapuj,
        /**
         * ⚠️⚠️ DOPÓKI NIE MAMY LISTY, NIE OSTRZEGAMY. `undefined` znaczy „nie wiemy", a nie
         * „nic nie ma" — inaczej okno otwarte przed dojściem odpowiedzi krzyczałoby, że wszystkie
         * centra są nowe.
         */
        znaneCentra:
          centra.data === undefined
            ? undefined
            : new Set(centra.data.podpowiedzi.map((c) => c.code.trim().toLowerCase())),
        grupy: (grupy.data ?? []).map((g) => ({ id: g.id, nazwa: g.name, kod: g.spsGroupNumber })),
        /**
         * ⚠️⚠️ ZGODA NA NADPISYWANIE LICZY SIĘ TYLKO PRZY ARKUSZU. Sam stan przełącznika nie
         * wystarcza: gdyby człowiek zaznaczył go na zakładce pliku i przeszedł na wklejanie,
         * wklejona lista też zaczęłaby nadpisywać — a to jest wprost wykluczone.
         */
        /**
         * ⚠️⚠️ DECYZJA ZMIENIONA 08.09.2026 I TRZEBA TO POWIEDZIEĆ WPROST. Stało tu
         * `zrodlo === 'plik' && nadpisuj`, bo 04.09.2026 rozstrzygnięto „to o nadpisywaniu to
         * raczej dajmy tylko do importu z arkusza". Wróciło inne zamówienie: przy zajętej karcie
         * we WKLEJCE ma być „możliwość nadpisania osoby (tak jak mówiliśmy — czasami osoba
         * dostaje kartę po kimś innym)". Przesłanka się zmieniła: to samo zdarza się przy liście
         * z maila, nie tylko przy arkuszu.
         *
         * ⚠️ DOMYŚLNIE NADAL WYŁĄCZONE i nadal jawnym kliknięciem — zmieniło się GDZIE wolno je
         * włączyć, nie to, czy trzeba.
         */
        nadpisujIstniejace: nadpisuj,
        /**
         * ⚠️⚠️ DOTYCZY OBU DRÓG, bo `0000AAA` przychodzi i we wklejce, i w arkuszu — a reguła
         * musi być jedna, inaczej ten sam plik wpuszczony dwiema drogami zakładałby ludzi pod
         * DWOMA różnymi kluczami.
         */
        usunZeraWiodace: usunZera,
      },
      wykluczone,
    ),
    [surowe, istniejace.data, grupy.data, centra.data, nadpisuj, grupyZastapuj, wykluczone, usunZera],
  );

  const wczytajPlik = async (plik: File) => {
    setBlad(null);
    try {
      const dane = await plik.arrayBuffer();
      const wynik = await czytajArkusze(dane);
      setArkusze(wynik);
      setNazwaPliku(plik.name);
      /** ⚠️ Pierwszy arkusz Z DANYMI, nie po prostu pierwszy — plik potrafi zaczynać się notatką. */
      const i = Math.max(0, wynik.findIndex((a) => a.wiersze.length > 1));
      setWybranyArkusz(i);
      const a = wynik[i];
      const maNaglowek = a !== undefined && czyPierwszyToNaglowek(a.wiersze);
      setZNaglowkiem(maNaglowek);
      setMapowanie(maNaglowek && a !== undefined ? podpowiedzMapowania(a.wiersze[0]) : {});
      /**
       * ⚠️⚠️ ZAKRES I ODZNACZENIA ZERUJEMY PRZY NOWYM PLIKU. Numery wierszy z poprzedniego
       * arkusza wskazują w nowym pliku zupełnie inne osoby — przeniesione odznaczenie zdjęłoby
       * ze wsadu kogoś, kogo nikt nie odznaczał, i wyglądałoby to na brak w pliku klienta.
       */
      setOdWiersza('');
      setDoWiersza('');
      setWykluczone(new Set());
    } catch (e) {
      setBlad(e instanceof Error ? `${t('imp.bladPliku')} ${e.message}` : t('imp.bladPliku'));
    }
  };

  const przestawArkusz = (i: number) => {
    setWybranyArkusz(i);
    const a = arkusze?.[i];
    const maNaglowek = a !== undefined && czyPierwszyToNaglowek(a.wiersze);
    setZNaglowkiem(maNaglowek);
    setMapowanie(maNaglowek && a !== undefined ? podpowiedzMapowania(a.wiersze[0]) : {});
    /** ⚠️ Jak przy nowym pliku: numery wierszy innego arkusza wskazują innych ludzi. */
    setOdWiersza('');
    setDoWiersza('');
    setWykluczone(new Set());
  };

  const przelaczWiersz = (nr: number) =>
    setWykluczone((prev) => {
      const nowe = new Set(prev);
      if (nowe.has(nr)) nowe.delete(nr);
      else nowe.add(nr);
      return nowe;
    });

  const odznacz = (nry: readonly number[]) => setWykluczone((prev) => new Set([...prev, ...nry]));

  /**
   * PRZYPISANIE KOLUMNY DO POLA.
   *
   * ⚠️⚠️ KOLUMNA ZDEJMOWANA Z POPRZEDNIEGO POLA. Bez tego jedna kolumna wpadałaby do dwóch pól
   * i nazwa grupy stawałaby się także imieniem i nazwiskiem — a w podglądzie wyglądałoby to na
   * dane klienta, nie na naszą pomyłkę.
   */
  /**
   * ═════════════════════════════════════════════════════════════════
   *  TABULATOR WPISUJE ZNAK, NIE PRZESKAKUJE DALEJ.
   *
   *  ⚠️⚠️ ZAMÓWIONE 04.09.2026: „zablokuj jakoś w okienku tekstu tabulator, żeby nie przeskakiwał
   *  dalej w oknie, tylko robił odstęp jak piszemy". I ma to sens, bo tabulator jest u nas
   *  ROZPOZNAWANYM SEPARATOREM — więc pisanie wsadu z ręki tabulatorami jest naturalne, a domyślne
   *  zachowanie przeglądarki wyrzucało kursor z pola w połowie wiersza.
   *
   *  ⚠️⚠️ DROGA UCIECZKI Z KLAWIATURY MUSI ZOSTAĆ, i to nie jest formalność: tabulator jest
   *  JEDYNYM sposobem opuszczenia pola tekstowego bez myszki. Przechwytujemy więc wyłącznie
   *  goły Tab — `Shift+Tab` dalej wychodzi wstecz, a `Escape` zamyka okno. Bez tego okno importu
   *  stawałoby się pułapką dla kogoś, kto nie używa myszki.
   *
   *  ⚠️ WSTAWIAMY PRZEZ `setRangeText`, nie przez sklejanie napisów: to ono zachowuje pozycję
   *  kursora i — co ważniejsze — historię cofania przeglądarki. Podmiana całej wartości przez
   *  `setTekst` kasuje `Ctrl+Z` w tym polu, a wsad wkleja się i poprawia właśnie cofaniem.
   * ═════════════════════════════════════════════════════════════════
   */
  const tabulatorWPolu = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key !== 'Tab' || e.shiftKey || e.ctrlKey || e.altKey || e.metaKey) return;
    e.preventDefault();
    const pole = e.currentTarget;
    pole.setRangeText('\t', pole.selectionStart, pole.selectionEnd, 'end');
    setTekst(pole.value);
  };

  /**
   * ═══════════════════════════════════════════════════════════════════════════════════════════
   *  WKLEJENIE TABELI Z MAILA — bierzemy ze schowka to samo, co bierze Excel.
   *
   *  ⚠️⚠️ ZGŁOSZONE 07.09.2026: „jak kopiuję tabelkę z maila i wklejam, to tekst jest w takim
   *  formacie" — każda komórka w osobnej linijce, między nimi linijki z samym tabulatorem. Okno
   *  widziało 20 wierszy po jednym polu zamiast 4 wierszy po 5 pól. I dalej: „jak wkleiłem do
   *  excela, a potem na stronę, to już było ok" — czyli Excel czyta to poprawnie.
   *
   *  ⚠️⚠️ BO SCHOWEK MA DWIE POSTACIE TEJ SAMEJ RZECZY. `text/html` to prawdziwa tabela,
   *  `text/plain` to jej rozpisanie na linijki zrobione przez klienta pocztowego. Excel bierze
   *  HTML; `<textarea>` dostaje sam `text/plain`. Bierzemy więc HTML i składamy z niego wiersze
   *  rozdzielone tabulatorami — czyli postać, którą to okno już rozumie.
   *
   *  ⚠️⚠️ HTML-A NIGDZIE NIE WSTAWIAMY. Reguła SKANUJE napis i oddaje zwykły tekst; ani jeden
   *  fragment schowka nie staje się elementem strony. Uzasadnienie i granice w `wklejka-tabeli.ts`.
   *
   *  ⚠️ GDY REGUŁA ODDA `null` — nie ruszamy niczego i przeglądarka wkleja po swojemu. Wklejka,
   *  której nie rozpoznajemy, ma trafić do pola nietknięta, a nie przepuszczona przez heurystykę.
   * ═══════════════════════════════════════════════════════════════════════════════════════════
   */
  const wklejenie = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const zlozone = zeSchowka(e.clipboardData.getData('text/html'), e.clipboardData.getData('text/plain'));
    if (zlozone === null) return;
    e.preventDefault();
    const pole = e.currentTarget;
    /** ⚠️ `setRangeText`, jak przy tabulatorze: zachowuje kursor i historię cofania w polu. */
    pole.setRangeText(zlozone, pole.selectionStart, pole.selectionEnd, 'end');
    setTekst(pole.value);
  };

  const przypisz = (klucz: KluczPola, kolumna: number | null) =>
    setMapowanie((m) => {
      const nowe: Mapowanie = { ...m };
      if (kolumna === null) delete nowe[klucz];
      else {
        for (const k of KLUCZE_POL) if (nowe[k] === kolumna) delete nowe[k];
        nowe[klucz] = kolumna;
      }
      return nowe;
    });

  const wyslij = async () => {
    setWysylka(true);
    setBlad(null);
    try {
      const wynik = await importujPracownikow(
        groupId,
        ocena.wiersze
          .map((w) => w.gotowy)
          .filter((g): g is NonNullable<typeof g> => g !== null)
          .map(({ tryb, ...reszta }) => ({
            ...reszta,
            /**
             * ⚠️⚠️ AUTOMATY TYLKO NOWYM PRACOWNIKOM. Lista automatów w tym oknie jest JEDNA na
             * cały wsad, więc wysłanie jej przy aktualizacji PRZEPISAŁOBY dostęp wszystkim osobom
             * z pliku — czyli podmiana nazwiska na karcie odbierałaby albo nadawała dostęp do
             * automatów, o którym w pliku nie ma ani słowa. Pole pominięte znaczy „nie ruszaj".
             */
            ...(tryb === 'NOWY' ? { machineIds: wybraneAutomaty } : {}),
          })),
        { nadpisujIstniejace: nadpisuj, grupyZastapuj },
      );
      toast.success(
        t('imp.zrobione')
          .replace('{ile}', String(wynik.ile))
          .replace('{nowych}', String(wynik.nowych))
          .replace('{aktualizacji}', String(wynik.aktualizacji)),
      );
      onSaved();
      onClose();
    } catch (e) {
      setBlad(e instanceof ApiError ? e.message : t('common.error'));
    } finally {
      setWysylka(false);
    }
  };

  /**
   * ⚠️⚠️ NUMERY LINII Z BŁĘDEM — dla rynny obok pola tekstowego. Liczone Z OCENY, nie z osobnego
   * sprawdzenia: druga reguła „co jest błędem" rozjechałaby się z podglądem, a wtedy czerwony
   * numer stałby przy wierszu, który w tabeli jest zielony.
   *
   * ⚠️ TYLKO PRZY WKLEJANIU: numery wierszy arkusza nie mają nic wspólnego z liniami tego pola.
   */
  /**
   * ⚠️ ILE WIERSZY MA ZERA WIODĄCE — liczba MUSI być widoczna przed zaznaczeniem przełącznika.
   * „Obetnij zera" bez tej liczby jest pytaniem, na które nie da się odpowiedzieć bez wczytania
   * się w tabelę; z nią widać od razu, czy w tym wsadzie jest o czym mówić.
   */
  const zZerami = useMemo(
    () => surowe.filter((x) => /^0[0-9A-Za-z]/.test(x.wiersz.karta.trim())).length,
    [surowe],
  );
  const bledneLinie = useMemo(
    () =>
      new Set(
        zrodlo === 'tekst'
          ? ocena.wiersze.filter((w) => !w.wykluczony && w.gotowy === null).map((w) => w.nr)
          : [],
      ),
    [ocena, zrodlo],
  );
  /**
   * ═════════════════════════════════════════════════════════════════
   *  KTO UŻYWA TEJ KARTY — z listy, którą ekran i tak ma.
   *
   *  ⚠️⚠️ `null` NIE ZNACZY „WOLNA", A „NIE WIEMY". Kadrę ciągniemy do 500 osób, więc na większej
   *  wyspie kolizję zgłosi dopiero instancja — i wtedy numer JEST zajęty, a nazwiska nie mamy.
   *  Okno musi wtedy powiedzieć „nie wiemy przez kogo", a nie pokazać pustych pól.
   * ═════════════════════════════════════════════════════════════════
   */
  const ktoUzywaKarty = (numer: string): ZajetaKarta | null => {
    const szukany = numer.trim().toLowerCase();
    const e = (istniejace.data?.rows ?? []).find((x) => x.spsUserNumber.trim().toLowerCase() === szukany);
    if (e === undefined) return null;
    return {
      numer: e.spsUserNumber,
      nazwa: e.name,
      alias: e.alias,
      centrum: e.costCenter,
      aktywny: e.active,
      grupy: e.groups.map((g) => g.name),
    };
  };

  /**
   * ⚠️⚠️ ILE WIERSZY WSADU TRAFIA W ZAJĘTĄ KARTĘ — tą liczbą okno mówi, ilu wierszy dotknie
   * przełącznik nadpisywania. Bez niej „nadpisz" przy jednym wierszu wyglądałoby na decyzję
   * o jednym człowieku, a jest decyzją o całym wsadzie.
   */
  const zajetychKart = useMemo(() => {
    const istn = new Set((istniejace.data?.rows ?? []).map((e) => e.spsUserNumber.trim().toLowerCase()));
    const widziane = new Set<string>();
    for (const w of ocena.wiersze) {
      const karta = w.pola.find((x) => x.klucz === 'karta')?.wartosc ?? '';
      const k = karta.trim().toLowerCase();
      if (k !== '' && istn.has(k)) widziane.add(k);
    }
    return widziane.size;
  }, [ocena, istniejace.data]);

  const powtorzone = useMemo(() => powtorzoneDoOdznaczenia(ocena), [ocena]);
  const bledne = useMemo(() => bledneDoOdznaczenia(ocena), [ocena]);
  const bezZmianRazem = useMemo(() => bezZmianDoOdznaczenia(ocena), [ocena]);
  const kartaLiczbowa = arkusz !== null && kartaMozeBycObcieta(mapowanie, arkusz.kolumnyLiczbowe);
  const wczytywanie = grupy.isPending || istniejace.isPending;

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="w-[min(72rem,calc(100vw-2rem))]" title={t('imp.tytul')}>
        <FormGrid>
          <Tabs
            zakladki={[
              { id: 'tekst' as Zrodlo, etykieta: t('imp.zakladkaTekst') },
              { id: 'plik' as Zrodlo, etykieta: t('imp.zakladkaPlik'), ikona: <FileSpreadsheet className="size-3.5" /> },
            ]}
            wybrana={zrodlo}
            onWybor={setZrodlo}
          />

          {zrodlo === 'tekst' && (
            <div className="space-y-2">
              {/*
                ⚠️⚠️ KOLEJNOŚĆ POZYCJI NAD POLEM, NIE W DOKUMENTACJI. To jedyna rzecz, którą trzeba
                wiedzieć przed wklejeniem, a wsad zmapowany o jedno pole w bok zakłada ludzi
                z przesuniętymi uprawnieniami. Numerujemy, bo o pozycji rozmawia się przez telefon.
              */}
              <ol className="flex flex-wrap gap-1 text-[11px] text-muted-foreground">
                {kolejnosc.map((klucz, i) => {
                  const p = POLA_IMPORTU.find((x) => x.klucz === klucz)!;
                  return (
                    <li key={klucz} className="flex items-center gap-1 rounded border border-border px-1.5 py-0.5">
                      {/*
                        ⚠️⚠️ STRZAŁKI, NIE PRZECIĄGANIE — i to jest inna decyzja niż przy arkuszu.
                        Tam przeciąga się KOLUMNĘ PLIKU na POLE (dwa różne zbiory, więc przeciągnięcie
                        mówi „to jest tamto"). Tu przestawia się KOLEJNOŚĆ jednej listy, a to jest ruch
                        o jedno miejsce, robiony patrząc na kolumny w mailu — strzałka wprost mówi, co
                        zrobi, działa z klawiatury i nie wymaga celowania w cel upuszczenia.
                      */}
                      <button
                        type="button"
                        className="rounded p-0.5 text-muted-foreground hover:bg-accent disabled:opacity-30"
                        disabled={i === 0}
                        aria-label={t('imp.wLewo').replace('{pole}', t(p.etykieta))}
                        onClick={() => setKolejnosc((k) => przestaw(k, i, -1))}
                      >
                        <ChevronLeft className="size-3" />
                      </button>
                      <span>
                        <span className="tabular-nums">{i + 1}.</span> {t(p.etykieta)}
                        {p.wymagane && <span className="text-[var(--destructive)]"> *</span>}
                        {p.domyslnie !== null && <span className="text-muted-foreground/70"> = {p.domyslnie}</span>}
                      </span>
                      <button
                        type="button"
                        className="rounded p-0.5 text-muted-foreground hover:bg-accent disabled:opacity-30"
                        disabled={i === kolejnosc.length - 1}
                        aria-label={t('imp.wPrawo').replace('{pole}', t(p.etykieta))}
                        onClick={() => setKolejnosc((k) => przestaw(k, i, 1))}
                      >
                        <ChevronRight className="size-3" />
                      </button>
                    </li>
                  );
                })}
                {/* ⚠️ DROGA POWROTNA: po kilku przestawieniach trudno odtworzyć układ domyślny z pamięci. */}
                {kolejnosc.join() !== KLUCZE_POL.join() && (
                  <li>
                    <button
                      type="button"
                      className="flex items-center gap-1 rounded px-1.5 py-0.5 text-[var(--primary)] hover:underline"
                      onClick={() => setKolejnosc([...KLUCZE_POL])}
                    >
                      <RotateCcw className="size-3" />
                      {t('imp.kolejnoscDomyslna')}
                    </button>
                  </li>
                )}
              </ol>
              {/*
                ═════════════════════════════════════════════════
                 RYNNA Z NUMERAMI WIERSZY — czerwona tam, gdzie jest błąd.

                 ⚠️⚠️ ZAMÓWIONE 07.09.2026: „fajnie jakby też dało się kolorować dany wiersz na
                 czerwono w momencie wystąpienia błędu — mamy już to na liście, ale spoko jakby
                 jeszcze w tekście się oznaczało".

                 ⚠️⚠️ W `<textarea>` NIE DA SIĘ POKOLOROWAć JEDNEJ LINIJKI — to jedno pole tekstowe,
                 nie dokument. Dlatego kolor idzie w RYNNIE obok, a nie w treści. Nakładka
                 z podświetleniem pod przezroczystym polem (jak w edytorach kodu) rozjeżdża się
                 przy pierwszym zawinięciu linii i przy każdej zmianie czcionki.

                 ⚠️⚠️ `wrap="off"` JEST TU WARUNKIEM POPRAWNOŚCI, nie stylem: z zawijaniem jedna
                 linijka zajmuje dwa wiersze na ekranie i numery przestają stać przy swoich
                 danych — czyli czerwony numer wskazywałby cudzy wiersz. Zamiast tego pole
                 przewija się w poziomie, a wklejka z maila i tak jest szeroka.

                 ⚠️ WYSOKOŚĆ LINII I ODŚRODKI MUSZĄ BYĆ TE SAME po obu stronach (`leading-5`, `py-2`),
                 bo to one trzymają numer na wysokości wiersza. Stąd powtórzone klasy, nie wspólny
                 kontener: pole i rynna przewijają się osobno i tylko pion jest zsynchronizowany.
                ══════════════════════════════════════════════════
              */}
              <div className="flex overflow-hidden rounded-md border border-border bg-background">
                <div
                  aria-hidden="true"
                  className="max-h-[11.5rem] shrink-0 select-none overflow-hidden border-r border-border bg-muted/40 py-2 pl-2 pr-1.5 text-right font-mono text-xs leading-5"
                  style={{ marginTop: -przewiniecie }}
                >
                  {tekst.split('\n').map((_, i) => {
                    const zly = bledneLinie.has(i + 1);
                    return (
                      <div
                        key={i}
                        className={cn('tabular-nums', zly ? 'font-semibold text-[var(--destructive)]' : 'text-muted-foreground/60')}
                      >
                        {i + 1}
                      </div>
                    );
                  })}
                </div>
                <textarea
                  ref={polePreRef}
                  value={tekst}
                  onChange={(e) => setTekst(e.target.value)}
                  onKeyDown={tabulatorWPolu}
                  onPaste={wklejenie}
                  onScroll={(e) => setPrzewiniecie(e.currentTarget.scrollTop)}
                  spellCheck={false}
                  wrap="off"
                  rows={8}
                  placeholder={'123456AB,Adam Nowak,ABCD1234,12345,MONTER,1,1,1\n0001'}
                  className="min-w-0 flex-1 resize-y bg-transparent px-2 py-2 font-mono text-xs leading-5 outline-none"
                />
              </div>
              {/*
                ⚠️ CZYM ROZDZIELILIŚMY — POKAZANE, NIE ZGADNIĘTE PO CICHU. Tabela z maila
                skopiowana do schowka jest rozdzielona TABULATORAMI, więc wykrywanie jest
                konieczne; ale gdy zgadniemy inaczej, niż człowiek myślał, musi to być widoczne.
              */}
              {tekst.trim() !== '' && (
                <p className="text-[11px] text-muted-foreground">
                  {/*
                    ⚠️⚠️ LICZBA WIERSZY W TEKŚCIE, nie liczba zaznaczonych. Ten napis opisuje ROZBIÓR
                    wklejki („rozdzielone tabulatorem — pięć wierszy"), więc po odznaczeniu
                    wszystkiego pokazywał „wiersze: 0" przy pięciu widocznych linijkach.
                  */}
                  {t(`imp.separator.${separator}`)} · {t('imp.wierszy').replace('{ile}', String(ocena.wszystkich))}
                </p>
              )}
            </div>
          )}

          {zrodlo === 'plik' && (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                <input
                  ref={plikRef}
                  type="file"
                  accept=".xlsx,.xlsm,.xls"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) void wczytajPlik(f);
                  }}
                />
                <Button type="button" variant="outline" size="sm" onClick={() => plikRef.current?.click()}>
                  <Upload className="size-3.5" />
                  {t('imp.wybierzPlik')}
                </Button>
                {nazwaPliku !== '' && <span className="text-xs text-muted-foreground">{nazwaPliku}</span>}
              </div>

              {/*
                ══════════════════════════════════════════════
                 ⚠️⚠️ NADPISYWANIE TYLKO TUTAJ, przy arkuszu, i domyślnie wyłączone — zamówione wprost.
                 Zakładka „Wklej listę" została czystym zakładaniem, więc wklejona lista NIGDY nie
                 tknie istniejącej kadry, niezależnie od tego, co jest tu zaznaczone.

                 ⚠️ DRUGI PRZEŁĄCZNIK MA SENS TYLKO PRZY WŁĄCZONYM PIERWSZYM: u nowego pracownika nie ma
                 czego zastępować. Dlatego jest wyłączony razem z nim, a nie tylko szary.
                ══════════════════════════════════════════════
              */}

              {arkusze !== null && (
                <div className="flex flex-wrap items-end gap-3">
                  {/* ⚠️ WSZYSTKIE ARKUSZE, także puste — zamówione wprost, i tak, żeby nie ukryć danych z drugiej zakładki. */}
                  <label className="text-xs">
                    <span className="mb-1 block text-muted-foreground">{t('imp.arkusz')}</span>
                    <Select value={String(wybranyArkusz)} onChange={(e) => przestawArkusz(Number(e.target.value))}>
                      {arkusze.map((a, i) => (
                        <option key={a.nazwa} value={i}>
                          {a.nazwa} ({a.wiersze.length})
                        </option>
                      ))}
                    </Select>
                  </label>
                  <label className="flex items-center gap-2 text-xs">
                    <input type="checkbox" checked={zNaglowkiem} onChange={(e) => setZNaglowkiem(e.target.checked)} />
                    {t('imp.pierwszyNaglowek')}
                  </label>
                  {/*
                    ════════════════════════════════════════════════
                     ⚠️⚠️ OGRANICZNIK ZAKRESU — zamówiony wprost: „zamiast 1 wiersza dostajemy 2500
                     wierszy, chyba trzeba jakiś ogranicznik, ile wierszy bierzemy".

                     ⚠️ RĘCZNE WYJŚCIE, NIE PIERWSZA OBRONA. Formułowe śmieci odsiewamy sami (kolumna
                     klucza z wartością logiczną albo pusta), więc w zmierzonym pliku ten zakres nie
                     jest potrzebny. Zostaje na pliki, których jeszcze nie widzieliśmy — i dlatego
                     puste pola znaczą „cały arkusz", a nie „nic".
                    ════════════════════════════════════════════════
                  */}
                  <label className="text-xs">
                    <span className="mb-1 block text-muted-foreground">{t('imp.odWiersza')}</span>
                    <Input
                      value={odWiersza}
                      onChange={(e) => setOdWiersza(e.target.value.replace(/\D/g, ''))}
                      placeholder={String((zNaglowkiem ? 1 : 0) + 1)}
                      inputMode="numeric"
                      className="h-8 w-20 text-xs"
                    />
                  </label>
                  <label className="text-xs">
                    <span className="mb-1 block text-muted-foreground">{t('imp.doWiersza')}</span>
                    <Input
                      value={doWiersza}
                      onChange={(e) => setDoWiersza(e.target.value.replace(/\D/g, ''))}
                      placeholder={String(arkusz?.wiersze.length ?? 0)}
                      inputMode="numeric"
                      className="h-8 w-20 text-xs"
                    />
                  </label>
                </div>
              )}

              {/*
                ⚠️⚠️ ILE WIERSZY ODSIALIŚMY I DLACZEGO. Ciche odsianie jest groźniejsze od błędu:
                „mam 50 osób, a weszło 48" to pytanie, na które portal musi umieć odpowiedzieć od razu.
                Osobno formułowe artefakty (wartość logiczna w kolumnie klucza — zmierzone: 2498
                wierszy w `Rękawiczki dostęp.xlsx`) i osobno zwykłe wiersze bez numeru karty.
              */}
              {(zArkusza.artefaktow > 0 || zArkusza.bezKlucza > 0) && (
                <p className="text-[11px] text-muted-foreground">
                  {zArkusza.artefaktow > 0 && t('imp.odsianeFormuly').replace('{ile}', String(zArkusza.artefaktow))}
                  {zArkusza.artefaktow > 0 && zArkusza.bezKlucza > 0 && ' · '}
                  {zArkusza.bezKlucza > 0 && t('imp.odsianeBezKarty').replace('{ile}', String(zArkusza.bezKlucza))}
                </p>
              )}

              {arkusz !== null && (
                <div className="space-y-2">
                  {/*
                    ⚠️⚠️ KOLUMNY JAKO KLOCKI DO PRZECIĄGNIĘCIA — zamówione („pozwolić wybrać te
                    niewymagane przez drag&drop kolumn"). OBOK zostaje lista rozwijana przy każdym
                    polu i to nie jest nadmiar: przeciąganie nie działa z klawiatury ani na
                    tablecie, a mapowanie to czynność, której nie wolno uzależnić od myszki.
                  */}
                  <p className="text-[11px] text-muted-foreground">{t('imp.przeciagnij')}</p>
                  <div className="flex flex-wrap gap-1">
                    {naglowki.map((h, i) => {
                      const uzyta = Object.values(mapowanie).includes(i);
                      return (
                        <span
                          key={i}
                          draggable
                          onDragStart={() => setCiagnieta(i)}
                          onDragEnd={() => setCiagnieta(null)}
                          className={`cursor-grab rounded border px-1.5 py-0.5 text-[11px] ${
                            uzyta ? 'border-border bg-muted text-muted-foreground' : 'border-primary/40 bg-primary/5'
                          }`}
                        >
                          {h === '' ? t('imp.kolumnaBezNazwy').replace('{nr}', String(i + 1)) : h}
                        </span>
                      );
                    })}
                  </div>

                  <div className="grid gap-1 sm:grid-cols-3">
                    {POLA_IMPORTU.map((p) => (
                      <div
                        key={p.klucz}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={() => {
                          if (ciagnieta !== null) przypisz(p.klucz, ciagnieta);
                          setCiagnieta(null);
                        }}
                        className={`rounded-md border p-1.5 text-xs ${
                          ciagnieta !== null ? 'border-dashed border-primary/60 bg-primary/5' : 'border-border'
                        }`}
                      >
                        <span className="block text-[11px] text-muted-foreground">
                          {t(p.etykieta)}
                          {p.wymagane && <span className="text-[var(--destructive)]"> *</span>}
                        </span>
                        <Select
                          className="mt-1 h-7 text-xs"
                          value={mapowanie[p.klucz] === undefined ? '' : String(mapowanie[p.klucz])}
                          onChange={(e) => przypisz(p.klucz, e.target.value === '' ? null : Number(e.target.value))}
                        >
                          <option value="">{p.domyslnie === null ? t('imp.bezKolumny') : `${t('imp.domyslnie')} ${p.domyslnie}`}</option>
                          {naglowki.map((h, i) => (
                            <option key={i} value={i}>
                              {h === '' ? t('imp.kolumnaBezNazwy').replace('{nr}', String(i + 1)) : h}
                            </option>
                          ))}
                        </Select>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/*
                ⚠️⚠️ OSTRZEŻENIE, KTÓREGO NIE DA SIĘ ZASTĄPIĆ POPRAWKĄ W KODZIE. Jeśli klient zapisał
                numer karty jako LICZBĘ, wiodących zer nie ma już w pliku — `000001234` jest tam
                wartością `1234`. Możemy tylko powiedzieć, że tak się stało, bo import założyłby
                człowieka pod innym kluczem niż ten na karcie.
              */}
              {kartaLiczbowa && (
                <div className="flex items-start gap-1.5 rounded-md border border-[var(--warning)]/40 bg-[var(--warning)]/10 p-2 text-xs">
                  <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
                  <span>{t('imp.kartaLiczbowa')}</span>
                </div>
              )}
            </div>
          )}

          {/*
            ══════════════════════════════════════════════════════════════════════════════════
             OBCINANIE ZER WIODĄCYCH — nad podglądem, bo dotyczy CAŁEGO wsadu, nie zakładki.

             ⚠️⚠️ ZAMÓWIONE 07.09.2026 i DOMYŚLNIE WYŁĄCZONE: „niektórzy klienci wysyłają 0000AAA
             […] a czytnik w automacie jest skonfigurowany na usuwanie zer wiodących".

             ⚠️⚠️ DOMYŚLNIE WYŁĄCZONE TO KONIECZNOŚĆ, NIE OSTROŻNOŚĆ. Arkusz Magny ma numery
             w postaci `000001234`, gdzie zera SĄ CZĘŚCIĄ KLUCZA — obcinanie ich założyłoby ludzi pod
             numerem `1234`, czyli pod innym kluczem niż ten wybity na karcie. To ta sama
             pomyłka, przed którą ostrzegamy przy liczbowej kolumnie w arkuszu, tylko nasza.

             ⚠️ POKAZUJEMY, ILU WIERSZY DOTKNIE, jeszcze przed zaznaczeniem — i podgląd pokazuje
             potem oba numery (w pliku → w SPS), bo zmieniamy KLUCZ NATURALNY.
            ══════════════════════════════════════════════════════════════════════════════════
          */}
          {/*
            ═════════════════════════════════════════════════════════════════════
             ⚠️⚠️ PRZENIESIONE 08.09.2026 Z ZAKŁADKI ARKUSZA DO CZĘŚCI WSPÓLNEJ. Stało pod wyborem
             pliku, bo 04.09.2026 nadpisywanie miało być „tylko do importu z arkusza". Nowe
             zamówienie objęło też wklejkę (zajęta karta zdarza się tak samo przy liście z maila),
             a jeden przycisk widoczny w obu zakładkach jest jedynym układem, w którym stan
             przełącznika zgadza się z tym, co zrobi zapis.

             ⚠️ DOMYŚLNIE WYŁĄCZONE — to się nie zmieniło.
            ═════════════════════════════════════════════════════════════════════
          */}
            {ocena.wszystkich > 0 && (
              <div className="space-y-1 rounded-md border border-border bg-muted/30 p-2">
                <label className="flex cursor-pointer items-start gap-2 text-xs">
                  <input
                    type="checkbox"
                    className="mt-0.5 size-3.5"
                    checked={nadpisuj}
                    onChange={(e) => setNadpisuj(e.target.checked)}
                  />
                  <span>
                    <span className="font-medium">{t('imp.nadpisuj')}</span>
                    <span className="block text-[11px] text-muted-foreground">{t('imp.nadpisujOpis')}</span>
                  </span>
                </label>
                <label className={`flex items-start gap-2 text-xs ${nadpisuj ? 'cursor-pointer' : 'opacity-50'}`}>
                  <input
                    type="checkbox"
                    className="mt-0.5 size-3.5"
                    disabled={!nadpisuj}
                    checked={grupyZastapuj}
                    onChange={(e) => setGrupyZastapuj(e.target.checked)}
                  />
                  <span>
                    <span className="font-medium">{t('imp.grupyZastapuj')}</span>
                    <span className="block text-[11px] text-muted-foreground">
                      {t(grupyZastapuj ? 'imp.grupyZastapujOpis' : 'imp.grupyDodajOpis')}
                    </span>
                  </span>
                </label>
              </div>
            )}
          {ocena.wszystkich > 0 && (
            <label className="flex cursor-pointer items-start gap-2 rounded-md border border-border bg-muted/30 p-2 text-xs">
              <input
                type="checkbox"
                className="mt-0.5 size-3.5"
                checked={usunZera}
                onChange={(e) => setUsunZera(e.target.checked)}
              />
              <span>
                <span className="font-medium">{t('imp.usunZera')}</span>
                {zZerami > 0 && (
                  <span className="block text-[11px] text-[var(--warning)]">
                    {t(usunZera ? 'imp.usunZeraObcieto' : 'imp.usunZeraSaZera').replace('{ile}', String(zZerami))}
                  </span>
                )}
              </span>
            </label>
          )}

          {/*
            ══════ PODGLĄD ══════
             ŻYJE, DOPÓKI TEKST MA WIERSZE, a nie dopóki są ZAZNACZONE.

             ⚠️⚠️ ZGŁOSZONE 08.09.2026: „jak wszystkie wiersze mają błąd i zrobimy «Odznacz wiersze
             z błędem», to nie można już w żaden sposób wrócić, trzeba wyłączyć i włączyć okienko
             importu". Bramka stała na `ocena.ile`, czyli na liczbie ZAZNACZONYCH — więc
             odznaczenie wszystkiego zabierało razem z podglądem przycisk „Przywróć wszystkie",
             który stoi WŚROD niego. Jednokierunkowe drzwi zrobione własną bramką.

             ⚠️ `wszystkich` TO LICZBA WIERSZY W TEKŚCIE i tylko ona opisuje, czy jest co pokazywać.
            ═══════════════════════════════════════════════════════════════
          */}
          {ocena.wszystkich > 0 && (
            <div className="space-y-2">
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <span className="font-medium">{t('imp.podglad')}</span>
                <Badge variant={ocena.mozna ? 'default' : 'warning'}>
                  {/* ⚠️ Przy odznaczonych mówimy „N z M" — sama liczba wchodzących gubiłaby to, że coś zdjęto. */}
                  {ocena.wykluczonych > 0
                    ? t('imp.wierszyZ').replace('{ile}', String(ocena.ile)).replace('{z}', String(ocena.wszystkich))
                    : t('imp.wierszy').replace('{ile}', String(ocena.ile))}
                </Badge>
                {/*
                  ⚠️⚠️ ROZBICIE NA ZAŁOŻENIA I NADPISANIA NAD TABELĄ. Wsad od firmy kadrowej jest
                  MIESZANY — nowi ludzie na nowych kartach i podmiany na starych w jednym pliku —
                  a to jedyna liczba, po której przed kliknięciem widać, ile osób zostanie NADPISANYCH.
                */}
                {ocena.aktualizacji > 0 && (
                  <Badge variant="warning">
                    {t('imp.rozbicie')
                      .replace('{nowych}', String(ocena.nowych))
                      .replace('{aktualizacji}', String(ocena.aktualizacji))}
                  </Badge>
                )}
                {wczytywanie && <Loader2 className="size-3.5 animate-spin text-muted-foreground" />}
              </div>

              {/*
                ══════════════════════════════════════════════════════
                 ZDEJMOWANIE WIERSZY ZE WSADU — zamówione: „czy możemy zrobić jakiś mechanizm, który
                 pozwoli odznaczyć błędny wiersz?".

                 ⚠️⚠️ HURTEM, NIE TYLKO PO JEDNYM. Przy 2500 wierszach odznaczanie z ręki nie jest
                 interfejsem, a plik, na którym to zmierzyliśmy, ma dokładnie tyle.

                 ⚠️⚠️ POWTÓRZENIA I BŁĘDY TO DWA OSOBNE PRZYCISKI, bo to dwie różne decyzje.
                 Powtórzenie rozwiązuje się WYBOREM (zostaje jeden wiersz na kartę), a wiersz z błędem
                 trzeba albo poprawić w pliku, albo świadomie pominąć — i to drugie znaczy, że ktoś
                 nie trafi na automat. Jeden przycisk na oba przypadki zamąciłby tę różnicę.

                 ⚠️ PORTAL NIE ODZNACZA SAM: przy dwóch wierszach na tę samą kartę tylko człowiek wie,
                 który z nich jest prawdziwy.
                ══════════════════════════════════════════════════════
              */}
              <div className="flex flex-wrap items-center gap-2">
                {powtorzone.length > 0 && (
                  <Button type="button" variant="outline" size="sm" onClick={() => odznacz(powtorzone)}>
                    {t('imp.odznaczPowtorzone').replace('{ile}', String(powtorzone.length))}
                  </Button>
                )}
                {/*
                  ⚠️⚠️ JEDNO KLIKNIĘCIE, NIE ZACHOWANIE. Wiersz bez zmian jest NIESZKODLIWY
                  (aktualizacja tymi samymi wartościami niczego nie psuje), więc portal go SAM
                  nie odsiewa — to zmieniałoby liczbę „zaimportowano N" bez pytania. Ale skoro
                  „nie ma sensu cokolwiek na takim pacjencie robić", odznaczenie ma być jednym
                  ruchem, a nie klikaniem po jednym.
                */}
                {bezZmianRazem.length > 0 && (
                  <Button type="button" variant="outline" size="sm" onClick={() => odznacz(bezZmianRazem)}>
                    {t('imp.odznaczBezZmian').replace('{ile}', String(bezZmianRazem.length))}
                  </Button>
                )}
                {bledne.length > 0 && (
                  <Button type="button" variant="outline" size="sm" onClick={() => odznacz(bledne)}>
                    {t('imp.odznaczBledne').replace('{ile}', String(bledne.length))}
                  </Button>
                )}
                {ocena.wykluczonych > 0 && (
                  <>
                    <span className="text-[11px] text-muted-foreground">
                      {t('imp.odznaczonych').replace('{ile}', String(ocena.wykluczonych))}
                    </span>
                    <Button type="button" variant="outline" size="sm" onClick={() => setWykluczone(new Set())}>
                      {t('imp.przywrocWszystkie')}
                    </Button>
                  </>
                )}
              </div>

              {/*
                ⚠️ POZIOMO PRZEWIJANA TABELA, nie zawijane pola. Dziewięć kolumn przy 200 wierszach
                musi dać się przejechać wzrokiem po jednej kolumnie — zawijanie zamieniłoby to
                w listę akapitów, w której nie widać, że jedna kolumna jest przesunięta.
              */}
              <div className="max-h-72 overflow-auto rounded-md border border-border">
                <table className="w-full text-[11px]">
                  <thead className="sticky top-0 bg-muted/80 backdrop-blur">
                    <tr>
                      {/* ⚠️ Ptaszek pierwszy, bo to on decyduje, czy resztę wiersza w ogóle czytamy. */}
                      <th className="w-6 px-2 py-1" />
                      <th className="px-2 py-1 text-left font-medium text-muted-foreground">{t('imp.wiersz')}</th>
                      {/* ⚠️ Kolumna trybu pokazuje się tylko wtedy, gdy jest co pokazywać — przy czystym zakładaniu byłaby szumem. */}
                      {ocena.aktualizacji > 0 && (
                        <th className="px-2 py-1 text-left font-medium text-muted-foreground">{t('imp.tryb')}</th>
                      )}
                      {POLA_IMPORTU.map((p) => (
                        <th key={p.klucz} className="px-2 py-1 text-left font-medium text-muted-foreground">
                          {t(p.etykieta)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {ocena.wiersze.slice(0, 200).map((w) => (
                      <tr key={w.nr} className={`border-t border-border ${w.wykluczony ? 'opacity-40' : ''}`}>
                        <td className="px-2 py-1">
                          {/*
                            ⚠️⚠️ ODZNACZONY WIERSZ ZOSTAJE W TABELI, tylko przygaszony. Zniknięcie po
                            kliknięciu nie pozwalałoby cofnąć decyzji ani sprawdzić, CO się zdjęło —
                            a przy zdublowanym arkuszu wybiera się między dwoma wierszami tej samej
                            karty i trzeba widzieć oba.
                          */}
                          <input
                            type="checkbox"
                            className="size-3.5"
                            checked={!w.wykluczony}
                            onChange={() => przelaczWiersz(w.nr)}
                            aria-label={t('imp.wezWiersz').replace('{nr}', String(w.nr))}
                          />
                        </td>
                        <td className="px-2 py-1 tabular-nums text-muted-foreground">{w.nr}</td>
                        {ocena.aktualizacji > 0 && (
                          <td className="px-2 py-1 align-top">
                            {/*
                              ⚠️⚠️ „BEZ ZMIAN" PRZED „NADPISANIE", bo to inna informacja: nadpisanie
                              mówi, że wiersz tknie istniejącą osobę, a „bez zmian" — że tknie ją
                              tymi samymi wartościami, czyli nie ma po co. Zamówione 08.09.2026
                              („czasem te gamonie nam 3x wysyłają tę samą osobę").
                            */}
                            {w.bezZmian ? (
                              <Badge variant="neutral" title={t('imp.bezZmianWiersz')}>
                                {t('imp.trybBezZmian')}
                              </Badge>
                            ) : w.tryb === 'AKTUALIZACJA' ? (
                              <Badge variant="warning">{t('imp.trybAktualizacja')}</Badge>
                            ) : (
                              <Badge variant="neutral">{t('imp.trybNowy')}</Badge>
                            )}
                          </td>
                        )}
                        {w.pola.map((pole) => (
                          <td key={pole.klucz} className="px-2 py-1 align-top">
                            {/*
                              ════════════════════════════════════════════════
                               ŻÓŁTY ZNACZEK: TO CENTRUM KOSZTOWE POWSTANIE PRZY TYM CZŁOWIEKU.
                               Zamówione 08.09.2026: „znaczek żółty ostrzeżenia, jeżeli danego centrum
                               kosztowego jeszcze nie ma i zostanie utworzone przy danym użytkowniku".

                               ⚠️⚠️ PRZED WARTOŚCIĄ, NIE PO NIEJ — komórka przy nadpisywaniu ma DWIE
                               linijki (nowa na zielono, stara na czerwono), więc znaczek dostawiony
                               na końcu wylądowałby pod nimi jako sierota. Z przodu trzyma się wiersza
                               w obu postaciach komórki.

                               ⚠️ MILCZY, GDY POLE MA BŁĄD — tam stoi już czerwony trójkąt. Dwa
                               trójkąty w jednej komórce mówiłyby o dwie rzeczy naraz i żaden nie
                               byłby czytelny.
                              ════════════════════════════════════════════════
                            */}
                            {pole.blad === null && pole.noweCentrum && (
                              <span
                                className="mr-1 align-top text-[var(--warning)]"
                                title={t('imp.noweCentrum')}
                              >
                                <AlertTriangle className="inline size-3" aria-hidden />
                              </span>
                            )}
                            {pole.blad !== null ? (
                              /**
                               * ════════════════════════════════════════════════
                               *  ⚠️⚠️ DWA BŁĘDY SĄ KLIKALNE, RESZTA NIE — zamówione 08.09.2026.
                               *  Zajęta karta i nieznana grupa to jedyne dwa, przy których
                               *  ekran ma odpowiedź, a człowiek jej nie ma: „przez kogo jest
                               *  zajęta" oraz „jakie grupy w ogóle są". Pozostałe (za długi
                               *  alias, zły znacznik) mówią wszystko w samym dymku, więc
                               *  robienie ich klikalnymi uczyłoby, że klik czasem nic nie robi.
                               * ════════════════════════════════════════════════
                               */
                              pole.klucz === 'karta' && pole.blad === 'imp.blad.kartaZajeta' ? (
                                <button
                                  type="button"
                                  className="text-[var(--destructive)] underline decoration-dotted underline-offset-2"
                                  title={t('imp.zajeta.klik')}
                                  onClick={() => setZajetaKarta(pole.wartosc ?? '')}
                                >
                                  {pole.wartosc ?? '—'} <AlertTriangle className="inline size-3" />
                                </button>
                              ) : pole.klucz === 'grupa' && pole.blad.startsWith('imp.blad.grupa') ? (
                                <button
                                  type="button"
                                  className="text-[var(--destructive)] underline decoration-dotted underline-offset-2"
                                  title={t('imp.grupy.klik')}
                                  onClick={() => setGrupaDlaWiersza({ nr: w.nr, wpisane: pole.wartosc ?? '' })}
                                >
                                  {pole.wartosc ?? '—'} <AlertTriangle className="inline size-3" />
                                </button>
                              ) : (
                                <span className="text-[var(--destructive)]" title={komunikat(t, pole.blad)}>
                                  {pole.wartosc ?? '—'} <AlertTriangle className="inline size-3" />
                                </span>
                              )
                            ) : /**
                               * ⚠️⚠️ „BEZ ZMIAN" TO NIE TO SAMO CO PUSTE, i to jest najważniejsza
                               * różnica w tym podglądzie po dodaniu nadpisywania. Przy nowym
                               * pracowniku puste pole znaczy „wpisz nic albo domyślną"; przy
                               * aktualizacji znaczy „zostaw to, co już ma". Jedna kreska na oba
                               * przypadki kazałaby zgadywać, czy plik z dwiema kolumnami wyczyści
                               * człowiekowi alias i centrum kosztowe.
                               */
                            /**
                             * ═══════════════════════════════════════════════════════════
                             *  STARE I NOWE W JEDNEJ KOMÓRCE — zamówione 08.09.2026: „nowa u góry
                             *  na zielono (może malutka strzałka w górę obok) i czerwone pod spodem
                             *  (strzałka w dół) […] wszystko w tym samym wierszu stary i nowy tekst".
                             *
                             *  ⚠️⚠️ POKAZUJEMY TYLKO RÓŻNICĘ. Wartość zgodna z bazą idzie jako zwykły
                             *  napis — dwie identyczne linijki obok siebie przy dwudziestu wierszach
                             *  zamieniłyby tabelę w szum, w którym prawdziwa zmiana przestaje
                             *  rzucać się w oczy.
                             *
                             *  ⚠️⚠️ KOLOR NIE NIESIE OCENY, tylko kierunek: zielone = to będzie
                             *  w bazie po zapisie, czerwone = to znika. Nie znaczy „dobrze/źle" —
                             *  nadpisanie może być dokładnie tym, po co ten wsad przyszedł.
                             * ═══════════════════════════════════════════════════════════
                             */
                            pole.obecne !== null &&
                              pole.wartosc !== null &&
                              pole.obecne.trim().toLowerCase() !== pole.wartosc.trim().toLowerCase() ? (
                              /** ⚠️ `inline-block`, nie `block` — żółty znaczek stoi przed tą parą w tej samej linijce. */
                              <span className="inline-block align-top leading-tight">
                                <span className="flex items-center gap-0.5 text-[var(--success)]">
                                  <ArrowUp className="size-2.5 shrink-0" aria-hidden />
                                  {pole.wartosc}
                                </span>
                                <span
                                  className="flex items-center gap-0.5 text-[var(--destructive)]"
                                  title={t('imp.bylo')}
                                >
                                  <ArrowDown className="size-2.5 shrink-0" aria-hidden />
                                  {pole.obecne === '' ? t('imp.puste') : pole.obecne}
                                </span>
                              </span>
                            ) : pole.zrodlo === 'BEZ_ZMIAN' ? (
                              <span className="text-muted-foreground/60" title={t('imp.bezZmianHint')}>
                                {t('imp.bezZmian')}
                              </span>
                            ) : pole.wartosc === null ? (
                              <span className="text-muted-foreground/50">—</span>
                            ) : (
                              /**
                               * ⚠️⚠️ WARTOŚĆ DOMYŚLNA WYGLĄDA INACZEJ NIŻ PODANA. To jest cała
                               * treść podglądu: „1" wzięte z wiersza i „1" wstawione przez nas
                               * znaczą to samo w bazie, ale COŚ INNEGO dla człowieka, który
                               * sprawdza, czy plik został dobrze zrozumiany.
                               */
                              <span
                                className={pole.zrodlo === 'DOMYSLNA' ? 'italic text-muted-foreground' : undefined}
                                /**
                                 * ⚠️⚠️ GDY COŚ POPRAWILIŚMY, DYMEK MÓWI CO BYŁO W PLIKU. Dotyczy
                                 * obcinania zer wiodących, czyli zmiany KLUCZA NATURALNEGO —
                                 * bez tego przełącznik cicho przepisuje numery kart, a różnicę
                                 * widać dopiero przy automacie.
                                 */
                                title={pole.przed === null ? undefined : t('imp.wPliku').replace('{co}', pole.przed)}
                              >
                                {pole.wartosc}
                                {pole.przed !== null && <span className="ml-1 text-[var(--warning)]">*</span>}
                              </span>
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {ocena.ile > 200 && (
                <p className="text-[11px] text-muted-foreground">
                  {t('imp.pokazanoCzesc').replace('{ile}', String(ocena.ile))}
                </p>
              )}

              {/* ⚠️ Zbiorcze powody odmowy — przy 200 wierszach nikt nie szuka ich po kolei w tabeli. */}
              {ocena.brakujaceGrupy.length > 0 && (
                <ModalError>
                  {t('imp.brakGrup')} {ocena.brakujaceGrupy.join(', ')}
                </ModalError>
              )}
              {ocena.duplikaty.length > 0 && (
                <ModalError>
                  {ocena.duplikaty
                    .slice(0, 5)
                    .map((d) => `${d.karta} (${t('imp.wWierszach')} ${d.wiersze.join(', ')})`)
                    .join(' · ')}
                </ModalError>
              )}
              {ocena.zaDuzy && <ModalError>{t('imp.zaDuzy').replace('{max}', String(MAX_WSAD))}</ModalError>}
            </div>
          )}

          {/* ══════ AUTOMATY ══════ */}
          {automaty.length > 0 && (
            <div className="space-y-1">
              <span className="text-xs font-medium">{t('imp.automaty')}</span>
              {/*
                ⚠️ DOMYŚLNIE WSZYSTKIE — tak samo jak przy zakładaniu jednego pracownika, i tak
                rozstrzygnięte. Pracownik bez ani jednego przypisania nie pobierze niczego nigdzie,
                a to wygląda jak awaria automatu, nie jak brak uprawnienia.

                ⚠️ ALE LICZBA MA BYĆ WIDOCZNA: przy 200 osobach i 20 automatach to 4000 wierszy
                przypisań w bazie klienta i człowiek ma prawo o tym wiedzieć PRZED kliknięciem.
              */}
              <p className="text-[11px] text-muted-foreground">
                {t('imp.automatowWybrano')
                  .replace('{ile}', String(wybraneAutomaty.length))
                  .replace('{z}', String(automaty.length))
                  .replace('{wierszy}', String(wybraneAutomaty.length * ocena.nowych))}
              </p>
              {/*
                ⚠️⚠️ MÓWIMY WPROST, ŻE AKTUALIZACJI TO NIE DOTYCZY. Lista automatów jest jedna na cały
                wsad, więc zastosowanie jej do nadpisywanych osób PRZEPISAŁOBY im dostęp — a plik
                z nazwiskiem i grupą nie mówi o automatach ani słowa. Bez tego zdania człowiek
                mógłby założyć, że odznaczenie automatu odbiera dostęp całemu wsadowi.
              */}
              {ocena.aktualizacji > 0 && (
                <p className="text-[11px] text-[var(--warning)]">
                  {t('imp.automatyTylkoNowym').replace('{ile}', String(ocena.aktualizacji))}
                </p>
              )}
              <div className="max-h-28 overflow-y-auto rounded-md border border-border p-1">
                {automaty.map((m) => (
                  <label key={m.id} className="flex cursor-pointer items-center gap-2 rounded px-1.5 py-0.5 text-xs hover:bg-accent/50">
                    <input
                      type="checkbox"
                      className="size-3.5"
                      checked={wybraneAutomaty.includes(m.id)}
                      onChange={(e) =>
                        setWybraneAutomaty((prev) => (e.target.checked ? [...prev, m.id] : prev.filter((x) => x !== m.id)))
                      }
                    />
                    {m.nazwa}
                  </label>
                ))}
              </div>
            </div>
          )}

          <ModalError>{blad}</ModalError>
        </FormGrid>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          {/*
            ⚠️⚠️ MARTWY, DOPÓKI CAŁY WSAD NIE JEST POPRAWNY. Import to JEDNA operacja config-down
            stosowana w jednej transakcji na bazie SPS — „wyślij te dobre" nie istnieje jako
            możliwość, a gdyby przycisk działał przy błędach, obiecywałby coś, czego nie zrobi.
          */}
          <Button type="button" disabled={!ocena.mozna || wysylka || wczytywanie} onClick={() => void wyslij()}>
            {wysylka && <Loader2 className="animate-spin" />}
            {t('imp.zaimportuj').replace('{ile}', String(ocena.ile))}
          </Button>
        </DialogFooter>
      </DialogContent>
      {/*
        ═════════════════════════════════════════════════════════════════
         OKNA DO DWÓCH KLIKALNYCH BŁĘDÓW — patrz `OknaBleduImportu.tsx`.

         ⚠️ DANE MAMY NA MIEJSCU: listę kadry i listę grup ekran i tak ciągnie, żeby pokazać kolizje
         w podglądzie. Żadnego nowego odczytu tu nie ma — brakowało wyłącznie pokazania tego, co
         portal już trzyma.
        ═════════════════════════════════════════════════════════════════
      */}
      {zajetaKarta !== null && (
        <OknoZajetejKarty
          karta={ktoUzywaKarty(zajetaKarta)}
          ileZajetych={zajetychKart}
          nadpisuj={nadpisuj}
          onNadpisuj={setNadpisuj}
          onClose={() => setZajetaKarta(null)}
        />
      )}

      {grupaDlaWiersza !== null && (
        <OknoWyboruGrupy
          grupy={(grupy.data ?? []).map((g) => ({
            id: g.id,
            nazwa: g.name,
            kod: g.spsGroupNumber,
            osob: g._count.members,
          }))}
          wpisane={grupaDlaWiersza.wpisane}
          /** ⚠️ Przy arkuszu nie ma gdzie wpisać — grupa przychodzi z KOLUMNY pliku. */
          mozeWpisac={zrodlo === 'tekst'}
          onWybierz={(kod) => {
            const i = kolejnosc.indexOf('grupa');
            /** ⚠️ Bez pozycji grupy w układzie nie ma gdzie wpisać — lepiej nic niż w cudze pole. */
            if (i >= 0) setTekst((poprzedni) => podmienPole(poprzedni, grupaDlaWiersza.nr, i, kod, separator));
            setGrupaDlaWiersza(null);
          }}
          onClose={() => setGrupaDlaWiersza(null)}
        />
      )}

    </Dialog>
  );
}

/**
 * Komunikat błędu pola: klucz tłumaczenia z opcjonalną wartością po kresce pionowej.
 *
 * ⚠️ WARTOŚĆ DOKLEJONA DO KLUCZA, nie osobne pole — bo reguła wsadu jest CZYSTA i nie zna `t()`.
 * Gdyby składała gotowe zdania, nie dałoby się jej użyć poza tym ekranem ani przetestować bez
 * słownika, a to jest ta część, która decyduje o zawartości bazy klienta.
 */
function komunikat(t: (k: string) => string, blad: string): string {
  const [klucz, wartosc] = blad.split('|');
  const zdanie = t(klucz);
  return wartosc === undefined ? zdanie : `${zdanie} ${wartosc}`;
}
